// SPDX-License-Identifier: AGPL-3.0-or-later
// Large purchases in the budget. Saving up sets money aside every month on
// the locked "purchases" budget line; financing adds a monthly payment event
// from the purchase date, which the budget already counts as scheduled.
import { addMonths, loanPayment, occurrences, planSavings, totalNeed, type Cents, type IsoDate, type Loan, type SavePlan } from "@almanac/domain";
import type { CalendarEvent, Purchase } from "./model.js";

/** The budget line large purchases are set aside on. */
export const PURCHASES_LINE = "purchases";

export const byPriority = (a: Purchase, b: Purchase) => a.priority - b.priority || a.name.localeCompare(b.name);

/** Purchases still being saved up for, highest priority first. */
export function activeSaves(purchases: readonly Purchase[]): Purchase[] {
  return purchases.filter((p) => p.mode === "save" && !p.boughtOn).sort(byPriority);
}

/** What to set aside this month: the limit if one is set, otherwise what the goals need. */
export function reserveAmount(purchases: readonly Purchase[], limit: Cents | null, today: IsoDate): Cents {
  return limit ?? totalNeed(activeSaves(purchases), today);
}

/**
 * How each save-up goal fares with `available` set aside a month (what the
 * budget line actually holds), in priority order.
 */
export function savingsPlans(purchases: readonly Purchase[], available: Cents, limit: Cents | null, today: IsoDate): Map<string, SavePlan> {
  const goals = activeSaves(purchases);
  // Automatic mode follows the goals unless the budget could not fit all of it.
  const monthly = limit === null && available >= totalNeed(goals, today) ? null : available;
  return new Map(planSavings(goals, monthly, today).map((p) => [p.id, p]));
}

export interface FinancePlan extends Loan {
  principal: Cents;
  months: number;
  firstPayment: IsoDate;
  lastPayment: IsoDate;
  /** Payments due on or before today. */
  made: number;
}

/** The loan behind a financed purchase: the price less the down payment, paid monthly from a month after buying. */
export function financePlan(p: Purchase, today: IsoDate): FinancePlan {
  const principal = Math.max(0, p.price - p.saved);
  const months = Math.max(1, p.termMonths ?? 60);
  const loan = loanPayment(principal, p.rate ?? 0, months);
  const firstPayment = addMonths(p.targetDate, 1);
  const lastPayment = addMonths(p.targetDate, months);
  const made = occurrences({ date: firstPayment, repeat: "monthly", until: lastPayment }, firstPayment, today).length;
  return { ...loan, principal, months, firstPayment, lastPayment, made };
}

/** The monthly payment event a financed purchase keeps in step, or null for one being saved up for. */
export function paymentEvent(p: Purchase, title: string, today: IsoDate): CalendarEvent | null {
  if (p.mode !== "finance" || !p.eventId) return null;
  const plan = financePlan(p, today);
  if (plan.payment <= 0) return null;
  return {
    id: p.eventId,
    title,
    date: plan.firstPayment,
    repeat: "monthly",
    until: plan.lastPayment,
    amount: plan.payment,
    category: p.category ?? "other",
    notes: "",
    hidden: false,
    purchaseId: p.id,
  };
}
